import { getSkillLevel } from './skills'
import type {
  ItemStack,
  SkillKey,
  Survivor,
  WorkstationId,
  WorkstationRuntimeState,
} from './types'

export type GeneratorWorkstationId = 'kitchen' | 'well'

type WeightedItem = {
  defId: string
  weight: number
}

export type WorkstationGenerationLevelConfig = {
  itemPool: WeightedItem[]
  baseCooldownMs: number
  maxClaimableItems: number
}

type WorkstationGenerationConfig = {
  skillKey: SkillKey
  cooldownReductionPerSkillLevel: number
  cooldownMultiplierFloor: number
  levels: Partial<Record<number, WorkstationGenerationLevelConfig>>
}

export const WORKSTATION_GENERATION: Record<
  GeneratorWorkstationId,
  WorkstationGenerationConfig
> = {
  kitchen: {
    skillKey: 'looting',
    cooldownReductionPerSkillLevel: 0.025,
    cooldownMultiplierFloor: 0.55,
    levels: {
      1: {
        itemPool: [
          { defId: 'food-bread', weight: 6 },
          { defId: 'food-crackers', weight: 5 },
          { defId: 'food-canned-meatballs', weight: 3 },
        ],
        baseCooldownMs: 22 * 60 * 1000,
        maxClaimableItems: 6,
      },
      2: {
        itemPool: [
          { defId: 'food-bread', weight: 4 },
          { defId: 'food-crackers', weight: 4 },
          { defId: 'food-canned-meatballs', weight: 5 },
          { defId: 'food-stamppot', weight: 2 },
        ],
        baseCooldownMs: 18 * 60 * 1000,
        maxClaimableItems: 8,
      },
      3: {
        itemPool: [
          { defId: 'food-canned-meatballs', weight: 5 },
          { defId: 'food-stamppot', weight: 4 },
          { defId: 'food-chocolate', weight: 1 },
        ],
        baseCooldownMs: 14 * 60 * 1000,
        maxClaimableItems: 10,
      },
    },
  },
  well: {
    skillKey: 'looting',
    cooldownReductionPerSkillLevel: 0.025,
    cooldownMultiplierFloor: 0.55,
    levels: {
      1: {
        itemPool: [
          { defId: 'water-bottle-half', weight: 7 },
          { defId: 'water-rain-jerrycan', weight: 2 },
        ],
        baseCooldownMs: 20 * 60 * 1000,
        maxClaimableItems: 6,
      },
      2: {
        itemPool: [
          { defId: 'water-bottle-half', weight: 5 },
          { defId: 'water-rain-jerrycan', weight: 4 },
        ],
        baseCooldownMs: 16 * 60 * 1000,
        maxClaimableItems: 8,
      },
      3: {
        itemPool: [
          { defId: 'water-bottle-half', weight: 3 },
          { defId: 'water-rain-jerrycan', weight: 5 },
          { defId: 'water-mineral-sealed', weight: 1 },
        ],
        baseCooldownMs: 12 * 60 * 1000,
        maxClaimableItems: 10,
      },
    },
  },
}

export function isGeneratorWorkstationId(
  workstationId: WorkstationId,
): workstationId is GeneratorWorkstationId {
  return workstationId === 'kitchen' || workstationId === 'well'
}

export function getWorkstationGenerationConfig(
  workstationId: GeneratorWorkstationId,
  level: number,
): WorkstationGenerationLevelConfig | null {
  const config = WORKSTATION_GENERATION[workstationId]
  const clamped = Math.max(1, Math.floor(level))
  return config.levels[clamped] ?? null
}

export function computeGenerationCooldownMs(
  workstationId: GeneratorWorkstationId,
  level: number,
  assignedSurvivor: Survivor | undefined,
): number | null {
  const levelConfig = getWorkstationGenerationConfig(workstationId, level)
  if (!levelConfig) return null
  if (!assignedSurvivor) return levelConfig.baseCooldownMs
  const config = WORKSTATION_GENERATION[workstationId]
  const looting = getSkillLevel(assignedSurvivor, config.skillKey)
  const skillLevel =
    workstationId === 'kitchen'
      ? Math.round(looting * 0.65 + getSkillLevel(assignedSurvivor, 'cooking') * 0.35)
      : looting
  const reduction = skillLevel * config.cooldownReductionPerSkillLevel
  const multiplier = Math.max(config.cooldownMultiplierFloor, 1 - reduction)
  return Math.max(1000, Math.round(levelConfig.baseCooldownMs * multiplier))
}

export function getGeneratedItemsCount(ws: WorkstationRuntimeState): number {
  return ws.generatedItems.reduce((sum, stack) => sum + stack.quantity, 0)
}

export function pickRandomGeneratedItemDefId(
  workstationId: GeneratorWorkstationId,
  level: number,
): string | null {
  const levelConfig = getWorkstationGenerationConfig(workstationId, level)
  if (!levelConfig || levelConfig.itemPool.length === 0) return null
  const totalWeight = levelConfig.itemPool.reduce(
    (sum, item) => sum + Math.max(0, item.weight),
    0,
  )
  if (totalWeight <= 0) return null
  let roll = Math.random() * totalWeight
  for (const item of levelConfig.itemPool) {
    roll -= Math.max(0, item.weight)
    if (roll <= 0) return item.defId
  }
  return levelConfig.itemPool[levelConfig.itemPool.length - 1]?.defId ?? null
}

export function pullOneGeneratedItem(
  generatedItems: ItemStack[],
  defId: string,
): { nextGeneratedItems: ItemStack[]; claimed: ItemStack[] } {
  const idx = generatedItems.findIndex((stack) => stack.defId === defId)
  if (idx < 0) return { nextGeneratedItems: generatedItems, claimed: [] }
  const stack = generatedItems[idx]
  if (stack.quantity <= 0) return { nextGeneratedItems: generatedItems, claimed: [] }
  const next = [...generatedItems]
  const nextQty = stack.quantity - 1
  if (nextQty <= 0) next.splice(idx, 1)
  else next[idx] = { ...stack, quantity: nextQty }
  return { nextGeneratedItems: next, claimed: [{ defId, quantity: 1 }] }
}
